  <template>
    <v-app :theme="isDarkMode ? 'dark' : 'light'">
      <NodesTopBar v-if="!showingPodsLayout"/>
      <PodsTopBar v-if="showingPodsLayout"/>

      <QuickPodSwitcher
        v-if="viewPodId && quickSwitcherPods.length > 1"
        :pods="quickSwitcherPods"
        :viewPodId="viewPodId"
        :visible="isQuickPodSwitcherVisible"
        @switchToPod="switchToPod"
        @requestHide="hideQuickPodSwitcher"
      />

      <snackbars/>

      <NodeSidemenu v-if="!showingPodsLayout"/>
      <PodSidemenu v-if="showingPodsLayout"/>

      <NodeDeletedDialog v-model="shouldShowDeletedNodeDialog"/>
      <PermissionsChangedDialog v-model="shouldShowPermissionsChangedDialog"/>

      <v-main scrollable>
        <v-container fluid class="pt-1 h-100">
          <RouterView/>
        </v-container>
      </v-main>

    </v-app>
</template>


<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useMainStore } from '@/stores/MainStore.js'
import { useNodeStore } from '@/stores/NodeStore.js'
import { usePodStore } from '@/stores/PodStore.js'
import { useTenantStore } from '@/stores/TenantStore.js'
import { useServiceStore } from '@/stores/ServiceStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import { useSystemStore } from '@/stores/SystemStore.js'
import { useNotificationStore } from '@/stores/NotificationStore.js'
import { useAnnouncementStore } from '@/stores/AnnouncementStore.js'

import NodesTopBar from "./components/topBars/NodesTopBar.vue"
import PodsTopBar from "./components/topBars/PodsTopBar.vue"
import Snackbars from "../components/Snackbars.vue"
import LayoutThemeMixin from '../components/LayoutThemeMixin.js'
import NodeSidemenu from "./components/nodeSidemenu/NodeSidemenu.vue"
import PodSidemenu from "./components/podSidemenu/PodSidemenu.vue"

import NodeDeletedDialog from '../../routes/nodes2/components/dialogs/NodeDeletedDialog.vue'
import PermissionsChangedDialog from '../../routes/nodes2/components/dialogs/PermissionsChangedDialog.vue'
import QuickPodSwitcher from '@/layouts2/mainLayout/components/topBars/QuickPodSwitcher.vue';
import { useSearchStore } from '@/stores/SearchStore.js';

export default {
  name: 'MainLayout',
  mixins: [LayoutThemeMixin ],
  components: {
    QuickPodSwitcher,
    NodesTopBar,
    PodsTopBar,
    Snackbars,
    NodeSidemenu,
    PodSidemenu,

    NodeDeletedDialog,
    PermissionsChangedDialog,
  },
  data() {
    return {
      shouldShowMovedNodeDialog: false,
      shouldShowDeletedNodeDialog: false,
      shouldShowPermissionsChangedDialog: false,
      topBarHeight: 0,
    };
  },
  async created() {
    // subscribe to push notifications on created()
    // to ensure that we will catch any notificaions
    const notificationStore = useNotificationStore();
    notificationStore.subscribe();
  },
  async mounted() {
    try {
      const authStore = useAuthStore();
      const user = await authStore.refreshDBUser();

      const isDarkMode = user?.settings?.theme === 'dark';
      const mainStore = useMainStore();
      if (window.localStorage.isDarkMode === undefined) {
        mainStore.setDarkMode(isDarkMode);
      }

      const nodeView = user?.settings?.view_folders === 'list' ? 'list' : 'grid';
      const nodeStore = useNodeStore();
      nodeStore._setNodeView(nodeView);

      this.setSubscriptionTimeout();

      const commonStore = useCommonStore();
      commonStore.refreshLanuages();

      nodeStore.refreshAccessPoints();

      const serviceStore = useServiceStore();
      serviceStore.refreshServices();

      if (authStore.rootNodeId) {
        const tenantStore = useTenantStore();
        tenantStore.refreshTenantSettings(authStore.rootNodeId);
      }

      const searchStore = useSearchStore();
      searchStore.refreshCurrentUserSearchHistory();

      // Fetch announcements on page load (always fetch to show button)
      if (authStore.rootNodeId) {
        const announcementStore = useAnnouncementStore();
        await announcementStore.fetchForPageLoad();
      }

    } catch (error) {
      console.log("ERROR:", error);
      setTimeout(() => {
        this.$router.push('/account/logout');
      }, 2000);
    }
  },
  beforeUnmount: function() {
    // remove subscription check timeout
    clearTimeout(this.subscriptionTimeoutID);

    const mainStore = useMainStore();
    mainStore.resetSnackbar();
  },
  watch: {
    getDbUser(newUser) {
      if (newUser) {
        this.refreshSubscription();
      }
    },
    getActiveAccesspoint: function(accesspoint) {
      this.$updateAbilities(accesspoint?.group?.abilities || []);
    },
    async getRootNodeId(newValue, oldValue) {
      if (newValue) {
        // console.log("old value/new value", oldValue, newValue);
        const tenantStore = useTenantStore();
        tenantStore.refreshTenantSettings(newValue).catch((e) => {
          console.error(`Failed to refresh tenant settings: ${e}`);
        });

        await this.refreshSubscription(newValue, true);
      }

      if (oldValue && newValue) {
        clearTimeout(this.subscriptionTimeoutID);
        this.checkSubscription();
      }
    },
    isMovedNodeDialog: function(newValue) {
      if (newValue) {
        this.shouldShowMovedNodeDialog = newValue;
      }
    },
    isDeletedNodeDialog: function(newValue) {
      if (newValue) {
        this.shouldShowDeletedNodeDialog = newValue;
      }
    },
    getForceLogout: function(newValue) {
      if (newValue) {
        this.shouldShowPermissionsChangedDialog = newValue;
      }
    },
  },
  methods: {
    setSubscriptionTimeout: function() {
      // Sets a timeout function to check if the subscription is still valid
      const authStore = useAuthStore();
      const expireTS = authStore.subscriptionExpireTimestamp;
      // console.log("EXPIRE TS:", expireTS);
      if (!expireTS) {
        return;
      }

      const maxTimeout = 2147483647; // 32-bit signied int, otherwise overflows
      const waitTime = Math.min(expireTS, maxTimeout);

      this.subscriptionTimeoutID = setTimeout(this.checkSubscription, waitTime);
      // setTimeout(this.checkSubscription, 10000);
    },
    checkSubscription: async function() {
      // Check if the subscription is still valid
      const authStore = useAuthStore();
      await authStore.refreshDBUser();

      if (this.isTenantExpired) {
        console.log("Tenant is expired")
        this.$router.push('/account/tenant-expired');
      } else {
        // console.log("tenant not expired");
        this.setSubscriptionTimeout();
      }
    },
    async refreshSubscription(contextNodeId = this.getRootNodeId, force = false) {
      const systemStore = useSystemStore();
      const subscription = systemStore.getSubscription;

      if (force || !subscription) {
        if (contextNodeId && this.$canControlTenantConfig()) {
          await systemStore.refreshSubscription(contextNodeId, force);
        }
      }
    },
    switchToPod(podId) {
      const podStore = usePodStore();

      podStore.toggleQuickSwitcher();
      const viewedPod = podStore.getViewPod;

      if (viewedPod.id !== podId) {
        this.$router.replace('/pod/' + podId);
      }
    },
    hideQuickPodSwitcher() {
      if (this.isQuickPodSwitcherVisible) {
        const podStore = usePodStore();
        podStore.toggleQuickSwitcher();
      }
    },
  },
  computed: {
    showingPodsLayout() {
      return this.$route.path.startsWith("/pod/");
    },
    getDbUser() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    getActiveAccesspoint() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint;
    },
    isMovedNodeDialog: function() {
      const nodeStore = useNodeStore();
      return nodeStore.isViewNodeMoved;
    },
    isDeletedNodeDialog: function() {
      const nodeStore = useNodeStore();
      return nodeStore.isViewNodeDeleted;
    },
    getRootNodeId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    isTenantExpired() {
      const authStore = useAuthStore();
      return authStore.tenantIsExpired;
    },
    getForceLogout: function() {
      const authStore = useAuthStore();
      return authStore.forceLogout;
    },
    quickSwitcherPods() {
      const podStore = usePodStore();
      return podStore.getPods;
    },
    viewPodId() {
      const podStore = usePodStore();
      const pod = podStore.getViewPod;
      return pod ? pod.id : null;
    },
    isQuickPodSwitcherVisible() {
      const podStore = usePodStore();
      return podStore.isQuickSwitcherVisible;
    },
    isDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
  }
}
</script>
